import React, { useEffect, useState } from "react";
import QuestionImage from "./components/QuestionImage";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpenCheck,
  Check,
  CheckCircle2,
  Download,
  FileQuestion,
  Hash,
} from "lucide-react";
import "./QuizSuccess.css";
import { exportQuizToWord } from "./utils/wordExportService";

export const deriveExportSubject = (questions = []) => {
  const subjects = Array.from(
    new Set(questions.map((question) => question?.subject).filter(Boolean))
  );
  if (subjects.length === 1) return subjects[0];
  if (subjects.length > 1) return "Mixed Subjects";
  return null;
};

const QuizSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isExportingWord, setIsExportingWord] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const { quizCode, quizTitle, selectedQuestions, quizSubject } = location.state || {};
  const questions = Array.isArray(selectedQuestions) ? selectedQuestions : [];
  const displaySubject = quizSubject || deriveExportSubject(questions);
  const totalMarks = questions.reduce(
    (total, question) => total + (parseFloat(question?.maxMark) || 0),
    0
  );
  const formattedTotalMarks = Number.isInteger(totalMarks)
    ? totalMarks
    : totalMarks.toFixed(1);

  // Handle export quiz to Word documents
  const handleExportWord = async () => {
    if (!selectedQuestions || selectedQuestions.length === 0) {
      alert("No questions available to export.");
      return;
    }

    setIsExportingWord(true);

    try {
      const subject = quizSubject || deriveExportSubject(selectedQuestions);

      // Export to Word documents
      const result = await exportQuizToWord(
        {
          quizCode: quizCode,
          subject: subject,
          quizTitle: quizTitle,
        },
        selectedQuestions
      );

      if (result.success) {
        alert("Word documents generated successfully! Check your downloads folder.");
      } else {
        alert(`Failed to generate Word documents: ${result.error}`);
      }
    } catch (error) {
      console.error("Error exporting quiz to Word:", error);
      alert("Failed to export test. Please try again.");
    } finally {
      setIsExportingWord(false);
    }
  };

  return (
    <main className="quiz-success-page">
      <div className="quiz-success-content">
        <section
          className="quiz-success-hero"
          role="status"
          aria-live="polite"
          aria-labelledby="quiz-success-title"
        >
          <span className="quiz-success-corner quiz-success-corner-top" aria-hidden="true" />
          <span className="quiz-success-corner quiz-success-corner-bottom" aria-hidden="true" />

          <div className="quiz-success-icon" aria-hidden="true">
            <Check size={30} strokeWidth={3} />
          </div>
          <p className="quiz-success-eyebrow">
            <CheckCircle2 size={15} aria-hidden="true" />
            Test created
          </p>
          <h1 id="quiz-success-title">Your test is ready</h1>
          <p className="quiz-success-intro">
            Your assessment has been created successfully and is ready to share
            with your students.
          </p>
        </section>

        <div className="quiz-success-overview">
          <section className="quiz-success-panel quiz-success-summary" aria-labelledby="quiz-summary-title">
            <div className="quiz-success-section-heading">
              <span className="quiz-success-heading-icon quiz-success-heading-icon-purple" aria-hidden="true">
                <BookOpenCheck size={21} />
              </span>
              <div>
                <p className="quiz-success-section-kicker">Assessment details</p>
                <h2 id="quiz-summary-title">Test summary</h2>
              </div>
            </div>

            <dl className="quiz-success-summary-grid">
              <div className="quiz-success-summary-item quiz-success-summary-item-title">
                <dt>Test title</dt>
                <dd>{quizTitle || "Untitled test"}</dd>
              </div>
              <div className="quiz-success-summary-item">
                <dt>Subject</dt>
                <dd>{displaySubject || "Not specified"}</dd>
              </div>
              <div className="quiz-success-summary-item">
                <dt>Questions</dt>
                <dd>{questions.length}</dd>
              </div>
              <div className="quiz-success-summary-item">
                <dt>Total marks</dt>
                <dd>{formattedTotalMarks}</dd>
              </div>
            </dl>
          </section>

          <section className="quiz-success-panel quiz-success-code-panel" aria-labelledby="quiz-code-title">
            <div className="quiz-success-code-icon" aria-hidden="true">
              <Hash size={22} />
            </div>
            <p className="quiz-success-section-kicker">Share with students</p>
            <h2 id="quiz-code-title">Test code</h2>
            <code className="quiz-success-code">{quizCode || "Not available"}</code>
            <p className="quiz-success-code-help">
              Students can enter this code to access the test.
            </p>
          </section>
        </div>

        <section className="quiz-success-panel quiz-success-next" aria-labelledby="quiz-next-title">
          <div>
            <p className="quiz-success-section-kicker">Next step</p>
            <h2 id="quiz-next-title">What would you like to do next?</h2>
            <p>Your test is saved. You can export a copy or return to your dashboard.</p>
          </div>
          <div className="quiz-success-actions">
            {questions.length > 0 && (
              <button
                type="button"
                onClick={handleExportWord}
                className="quiz-success-action quiz-success-export-button"
                disabled={isExportingWord}
              >
                <Download size={18} aria-hidden="true" />
                {isExportingWord ? "Exporting…" : "Export Word Docs"}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="quiz-success-action quiz-success-dashboard-button"
            >
              <ArrowLeft size={18} aria-hidden="true" />
              Back to Dashboard
            </button>
          </div>
        </section>

        <section className="quiz-success-panel quiz-success-questions" aria-labelledby="included-questions-title">
          <div className="quiz-success-questions-header">
            <div className="quiz-success-section-heading">
              <span className="quiz-success-heading-icon quiz-success-heading-icon-blue" aria-hidden="true">
                <FileQuestion size={21} />
              </span>
              <div>
                <p className="quiz-success-section-kicker">Created assessment</p>
                <h2 id="included-questions-title">Included questions</h2>
              </div>
            </div>
            <span className="quiz-success-count">
              {questions.length} {questions.length === 1 ? "question" : "questions"}
            </span>
          </div>

          {questions.length > 0 ? (
            <div className="quiz-success-table-wrap">
              <table className="quiz-success-table">
                <caption className="quiz-success-sr-only">
                  Questions included in the newly created test
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Question</th>
                    <th scope="col">Spec code</th>
                    <th scope="col">Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question) => (
                    <tr key={question.id}>
                      <td data-label="Question">
                        {question.questionText ? (
                          question.questionText
                        ) : (
                          <span className="quiz-success-image-label">Image-based question</span>
                        )}
                        {(question.questionImageURL || question.questionMedia) && (
                          <QuestionImage question={question} context={{ type: "question-owner" }}
                            alt="Question" className="quiz-success-question-image" />
                        )}
                      </td>
                      <td data-label="Spec code">{question.specCode || "—"}</td>
                      <td data-label="Subject">{question.subject || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="quiz-success-no-questions">
              No questions selected or available.
            </p>
          )}
        </section>

      </div>
    </main>
  );
};

export default QuizSuccess;
